/**
 * FormationQuestionnaireManager
 *
 * Composant CRM pour gérer les questionnaires de formation d'un audit :
 *  - Créer / modifier / supprimer un questionnaire (Pré, Post, Intermédiaire)
 *  - Créer / modifier / supprimer des questions
 *  - Créer / modifier / supprimer des réponses (avec marquage de la bonne réponse)
 *  - Consulter les résultats des apprenants
 *
 * Usage :
 *   <FormationQuestionnaireManager auditId={123} />
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BarChartIcon from '@mui/icons-material/BarChart';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

import { formationQuestionnaireApi } from '../../../api/formation-questionnaire-api';
import { useAuth } from '../../../hooks/use-auth';

// ---------------------------------------------------------------------------
// Constantes
// ---------------------------------------------------------------------------

const QUESTIONNAIRE_TYPES = [
  { value: 'pre', label: 'Pré-Formation', color: 'primary', maxQuestions: 20, minQuestions: 1 },
  { value: 'post', label: 'Post-Formation', color: 'success', maxQuestions: 20, minQuestions: 1 },
  { value: 'intermediate', label: 'Évaluation Intermédiaire', color: 'warning', maxQuestions: 5, minQuestions: 1 },
];

const TYPE_META = Object.fromEntries(QUESTIONNAIRE_TYPES.map((t) => [t.value, t]));

const LEVEL_COLORS = {
  Apprenti: '#f97316',
  Intermédiaire: '#8b5cf6',
  Confirmé: '#22c55e',
  Expert: '#3b82f6',
};

// ---------------------------------------------------------------------------
// FormationQuestionnaireManager
// ---------------------------------------------------------------------------

export default function FormationQuestionnaireManager({ auditId }) {
  const { user } = useAuth();
  const adminId = user?.admin_id;

  const [questionnaires, setQuestionnaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Dialog création questionnaire
  const [openCreate, setOpenCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', type: 'pre', tour: 1 });
  const [creating, setCreating] = useState(false);

  const handleOpenCreate = () => {
    const usedUniqueTypes = new Set(
      questionnaires.filter((q) => q.type === 'pre' || q.type === 'post').map((q) => q.type)
    );
    const usedTours = new Set(
      questionnaires.filter((q) => q.type === 'intermediate').map((q) => q.tour)
    );
    const availableTypes = QUESTIONNAIRE_TYPES.filter(
      (t) => t.value === 'intermediate' || !usedUniqueTypes.has(t.value)
    );
    const defaultType = availableTypes[0]?.value || 'intermediate';
    const defaultTour = [1, 2].find((t) => !usedTours.has(t)) ?? 1;
    setCreateForm({ name: '', type: defaultType, tour: defaultTour });
    setOpenCreate(true);
  };

  // Dialog confirmation suppression questionnaire
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  // Dialog détail questionnaire (questions / réponses)
  const [detailQId, setDetailQId] = useState(null);
  const [detailQ, setDetailQ] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Vue résultats
  const [resultsQId, setResultsQId] = useState(null);
  const [results, setResults] = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);

  // ---------------------------------------------------------------------------
  // Chargement de la liste
  // ---------------------------------------------------------------------------

  const loadQuestionnaires = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await formationQuestionnaireApi.getAllQuestionnaires(adminId, auditId);
      setQuestionnaires(Array.isArray(data) ? data : []);
    } catch {
      setError('Impossible de charger les questionnaires.');
    } finally {
      setLoading(false);
    }
  }, [adminId, auditId]);

  useEffect(() => { loadQuestionnaires(); }, [loadQuestionnaires]);

  // ---------------------------------------------------------------------------
  // Chargement du détail
  // ---------------------------------------------------------------------------

  const loadDetail = useCallback(async (qId) => {
    setLoadingDetail(true);
    try {
      const data = await formationQuestionnaireApi.getQuestionnaireById(adminId, qId);
      setDetailQ(data);
    } catch {
      setError('Impossible de charger le questionnaire.');
    } finally {
      setLoadingDetail(false);
    }
  }, [adminId]);

  const openDetail = (qId) => {
    setDetailQId(qId);
    setDetailQ(null);
    loadDetail(qId);
  };

  const closeDetail = () => {
    setDetailQId(null);
    setDetailQ(null);
  };

  // ---------------------------------------------------------------------------
  // Création questionnaire
  // ---------------------------------------------------------------------------

  const handleCreate = async () => {
    setCreating(true);
    try {
      await formationQuestionnaireApi.createQuestionnaire(
        adminId,
        auditId,
        createForm.name,
        createForm.type,
        createForm.type === 'intermediate' ? createForm.tour : null,
      );
      setOpenCreate(false);
      setCreateForm({ name: '', type: 'pre', tour: 1 });
      loadQuestionnaires();
    } catch {
      setError('Erreur lors de la création.');
    } finally {
      setCreating(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Suppression questionnaire
  // ---------------------------------------------------------------------------

  const handleDeleteQuestionnaire = (qId) => {
    setDeleteTargetId(qId);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDeleteQuestionnaire = async () => {
    try {
      await formationQuestionnaireApi.deleteQuestionnaire(adminId, deleteTargetId);
      if (detailQId === deleteTargetId) closeDetail();
      loadQuestionnaires();
    } catch {
      setError('Erreur lors de la suppression.');
    } finally {
      setDeleteConfirmOpen(false);
      setDeleteTargetId(null);
    }
  };

  // ---------------------------------------------------------------------------
  // Résultats
  // ---------------------------------------------------------------------------

  const openResults = async (qId) => {
    setResultsQId(qId);
    setLoadingResults(true);
    try {
      const data = await formationQuestionnaireApi.getQuestionnaireResults(adminId, qId);

      // Dédupliquer : garder la dernière tentative par apprenant unique
      const allResults = data.results || [];
      const byLearner = {};
      for (const r of allResults) {
        const key = r.user_id != null
          ? String(r.user_id)
          : `${r.user_firstname}__${r.user_lastname}`;
        const existing = byLearner[key];
        if (!existing || new Date(r.submitted_at) > new Date(existing.submitted_at)) {
          byLearner[key] = r;
        }
      }
      let dedupedResults = Object.values(byLearner);

      // Pour un questionnaire intermédiaire, remplacer le niveau par celui du pré-formation
      const clickedQ = questionnaires.find((q) => q.id === qId);
      if (clickedQ?.type === 'intermediate') {
        const preQ = questionnaires.find((q) => q.type === 'pre');
        if (preQ) {
          const preData = await formationQuestionnaireApi.getQuestionnaireResults(adminId, preQ.id);
          // Garder la dernière soumission pré-formation par apprenant
          const preByLearner = {};
          for (const r of (preData.results || [])) {
            const key = r.user_id != null
              ? String(r.user_id)
              : `${r.user_firstname}__${r.user_lastname}`;
            if (!preByLearner[key] || new Date(r.submitted_at) > new Date(preByLearner[key].submitted_at)) {
              preByLearner[key] = r;
            }
          }
          // Substituer le niveau pré-formation dans chaque résultat intermédiaire
          dedupedResults = dedupedResults.map((r) => {
            const key = r.user_id != null
              ? String(r.user_id)
              : `${r.user_firstname}__${r.user_lastname}`;
            const preLevel = preByLearner[key]?.level;
            return preLevel ? { ...r, level: preLevel } : r;
          });
        }
      }

      // Recalculer les stats à partir des apprenants dédupliqués
      const totalLearners = dedupedResults.length;
      const averageScore = totalLearners === 0
        ? 0
        : Math.round(dedupedResults.reduce((sum, r) => sum + (r.score_percent || 0), 0) / totalLearners * 10) / 10;
      const levelDistribution = {};
      for (const r of dedupedResults) {
        if (r.level) levelDistribution[r.level] = (levelDistribution[r.level] || 0) + 1;
      }

      setResults({
        ...data,
        results: dedupedResults,
        stats: {
          ...(data.stats || {}),
          total_learners: totalLearners,
          average_score: averageScore,
          level_distribution: levelDistribution,
        },
      });
    } catch {
      setError('Impossible de charger les résultats.');
    } finally {
      setLoadingResults(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" fontWeight="bold">Questionnaires de Formation</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
          Nouveau questionnaire
        </Button>
      </Stack>

      {/* Liste des questionnaires */}
      {questionnaires.length === 0 ? (
        <Alert severity="info">Aucun questionnaire créé pour cette formation.</Alert>
      ) : (
        questionnaires.map((q) => (
          <QuestionnaireRow
            key={q.id}
            questionnaire={q}
            onEdit={() => openDetail(q.id)}
            onDelete={() => handleDeleteQuestionnaire(q.id)}
            onResults={() => openResults(q.id)}
          />
        ))
      )}

      {/* Dialog détail questionnaire (questions / réponses) */}
      <Dialog open={!!detailQId} onClose={closeDetail} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            {detailQ && (
              <Chip
                label={TYPE_META[detailQ.type]?.label || detailQ.type}
                color={TYPE_META[detailQ.type]?.color || 'default'}
                size="small"
              />
            )}
            <span>{detailQ?.name || '…'}</span>
          </Stack>
          <IconButton size="small" onClick={closeDetail}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loadingDetail ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : detailQ ? (
            <QuestionnaireDetail
              questionnaire={detailQ}
              adminId={adminId}
              onRefresh={() => { loadDetail(detailQId); loadQuestionnaires(); }}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetail}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog résultats */}
      {resultsQId && (
        <ResultsDialog
          open={!!resultsQId}
          onClose={() => { setResultsQId(null); setResults(null); }}
          results={results}
          loading={loadingResults}
          questionnaireName={questionnaires.find((q) => q.id === resultsQId)?.name || ''}
        />
      )}

      {/* Dialog confirmation suppression questionnaire */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>Supprimer ce questionnaire et toutes ses questions ? Cette action est irréversible.</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Annuler</Button>
          <Button color="error" variant="contained" onClick={handleConfirmDeleteQuestionnaire}>Supprimer</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog création questionnaire */}
      <Dialog open={openCreate} onClose={() => setOpenCreate(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nouveau questionnaire</DialogTitle>
        <DialogContent>
          {(() => {
            const usedUniqueTypes = new Set(
              questionnaires.filter((q) => q.type === 'pre' || q.type === 'post').map((q) => q.type)
            );
            const availableTypes = QUESTIONNAIRE_TYPES.filter(
              (t) => t.value === 'intermediate' || !usedUniqueTypes.has(t.value)
            );
            // Si le type sélectionné n'est plus disponible, basculer sur le premier disponible
            const effectiveType = availableTypes.find((t) => t.value === createForm.type)
              ? createForm.type
              : availableTypes[0]?.value || 'intermediate';

            const usedTours = new Set(
              questionnaires.filter((q) => q.type === 'intermediate').map((q) => q.tour)
            );
            const availableTours = [1, 2].filter((t) => !usedTours.has(t));

            return (
              <Stack spacing={2} mt={1}>
                <TextField
                  label="Nom du questionnaire"
                  value={createForm.name}
                  onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                  fullWidth
                />
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={effectiveType}
                    label="Type"
                    onChange={(e) => setCreateForm((f) => ({ ...f, type: e.target.value }))}
                  >
                    {availableTypes.map((t) => (
                      <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {effectiveType === 'intermediate' && (
                  <FormControl fullWidth>
                    <InputLabel>Tour</InputLabel>
                    <Select
                      value={createForm.tour}
                      label="Tour"
                      onChange={(e) => setCreateForm((f) => ({ ...f, tour: e.target.value }))}
                    >
                      {[1, 2].map((t) => (
                        <MenuItem key={t} value={t} disabled={usedTours.has(t)}>
                          Tour {t}{usedTours.has(t) ? ' (déjà créé)' : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <Alert severity="info" sx={{ fontSize: 12 }}>
                  {effectiveType === 'intermediate'
                    ? `Un questionnaire intermédiaire peut contenir jusqu'à 5 questions. Il s'affichera à l'apprenant après le Tour sélectionné.`
                    : "Un questionnaire Pré/Post-Formation peut contenir jusqu'à 20 questions. Une seule instance par formation."}
                </Alert>

                {effectiveType === 'intermediate' && availableTours.length === 0 && (
                  <Alert severity="warning" sx={{ fontSize: 12 }}>
                    Les questionnaires pour Tour 1 et Tour 2 ont déjà été créés.
                  </Alert>
                )}
              </Stack>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreate(false)}>Annuler</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating || !createForm.name}
          >
            {creating ? <CircularProgress size={20} /> : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ===========================================================================
// QuestionnaireRow : ligne de la liste
// ===========================================================================

function QuestionnaireRow({ questionnaire, onEdit, onDelete, onResults }) {
  const meta = TYPE_META[questionnaire.type] || {};
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
        mb: 1,
        cursor: 'pointer',
        bgcolor: 'background.paper',
        '&:hover': { bgcolor: 'action.hover' },
      }}
      onClick={onEdit}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip label={meta.label || questionnaire.type} color={meta.color || 'default'} size="small" />
          {questionnaire.type === 'intermediate' && questionnaire.tour && (
            <Chip label={`Tour ${questionnaire.tour}`} size="small" variant="outlined" color="warning" />
          )}
          <Typography fontWeight="medium">{questionnaire.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            ({questionnaire.question_count} question{questionnaire.question_count !== 1 ? 's' : ''})
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Gérer les questions">
            <IconButton size="small" color="primary" onClick={onEdit}><EditIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Résultats apprenants">
            <IconButton size="small" onClick={onResults}><BarChartIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Supprimer">
            <IconButton size="small" color="error" onClick={onDelete}><DeleteIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Box>
  );
}

// ===========================================================================
// QuestionnaireDetail : vue détail avec questions et réponses
// ===========================================================================

function QuestionnaireDetail({ questionnaire, adminId, onRefresh }) {
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(questionnaire.name);
  const [savingName, setSavingName] = useState(false);

  const [addingQuestion, setAddingQuestion] = useState(false);
  const [newQuestionText, setNewQuestionText] = useState('');
  const [savingQuestion, setSavingQuestion] = useState(false);

  const meta = TYPE_META[questionnaire.type] || {};
  const questionCount = questionnaire.questions?.length || 0;
  const canAddQuestion = questionCount < (meta.maxQuestions || 20);

  const handleSaveName = async () => {
    setSavingName(true);
    try {
      await formationQuestionnaireApi.modifyQuestionnaire(adminId, questionnaire.id, { name: nameValue });
      setEditingName(false);
      onRefresh();
    } finally {
      setSavingName(false);
    }
  };

  const handleAddQuestion = async () => {
    if (!newQuestionText.trim()) return;
    setSavingQuestion(true);
    try {
      await formationQuestionnaireApi.createQuestion(adminId, questionnaire.id, newQuestionText, questionCount);
      setNewQuestionText('');
      setAddingQuestion(false);
      onRefresh();
    } finally {
      setSavingQuestion(false);
    }
  };

  return (
    <Box>
      {/* Renommer le questionnaire */}
      <Stack direction="row" spacing={1} alignItems="center" mb={2}>
        {editingName ? (
          <>
            <TextField size="small" value={nameValue} onChange={(e) => setNameValue(e.target.value)} sx={{ flexGrow: 1 }} />
            <Button size="small" variant="contained" onClick={handleSaveName} disabled={savingName}>
              {savingName ? <CircularProgress size={16} /> : 'OK'}
            </Button>
            <Button size="small" onClick={() => setEditingName(false)}>Annuler</Button>
          </>
        ) : (
          <Tooltip title="Renommer le questionnaire">
            <Button size="small" startIcon={<EditIcon />} onClick={() => setEditingName(true)}>
              Renommer
            </Button>
          </Tooltip>
        )}
      </Stack>

      <Stack direction="row" spacing={1} mb={2}>
        <Alert severity={questionCount < (meta.minQuestions || 1) ? 'warning' : 'success'} sx={{ py: 0 }}>
          {questionCount} / {meta.maxQuestions} question{questionCount !== 1 ? 's' : ''}
          {questionCount < (meta.minQuestions || 1) && ` (minimum : ${meta.minQuestions})`}
        </Alert>
      </Stack>

      <Divider sx={{ mb: 2 }} />

      {/* Questions */}
      {(questionnaire.questions || []).map((q, idx) => (
        <QuestionItem
          key={q.id}
          question={q}
          index={idx}
          adminId={adminId}
          onRefresh={onRefresh}
        />
      ))}

      {/* Ajout question */}
      {canAddQuestion && (
        addingQuestion ? (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
            <TextField
              label="Texte de la question"
              value={newQuestionText}
              onChange={(e) => setNewQuestionText(e.target.value)}
              fullWidth
              multiline
              rows={2}
              autoFocus
            />
            <Stack direction="row" spacing={1} mt={1}>
              <Button variant="contained" size="small" onClick={handleAddQuestion} disabled={savingQuestion || !newQuestionText.trim()}>
                {savingQuestion ? <CircularProgress size={16} /> : 'Ajouter'}
              </Button>
              <Button size="small" onClick={() => setAddingQuestion(false)}>Annuler</Button>
            </Stack>
          </Box>
        ) : (
          <Button startIcon={<AddIcon />} sx={{ mt: 1 }} onClick={() => setAddingQuestion(true)}>
            Ajouter une question
          </Button>
        )
      )}
    </Box>
  );
}

// ===========================================================================
// QuestionItem : une question avec ses réponses
// ===========================================================================

function QuestionItem({ question, index, adminId, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [questionText, setQuestionText] = useState(question.question);
  const [saving, setSaving] = useState(false);

  const [deleteQConfirmOpen, setDeleteQConfirmOpen] = useState(false);

  const [addingAnswer, setAddingAnswer] = useState(false);
  const [newAnswerText, setNewAnswerText] = useState('');
  const [newAnswerCorrect, setNewAnswerCorrect] = useState(false);
  const [savingAnswer, setSavingAnswer] = useState(false);

  const handleSaveQuestion = async () => {
    setSaving(true);
    try {
      await formationQuestionnaireApi.modifyQuestion(adminId, question.id, questionText);
      setEditing(false);
      onRefresh();
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuestion = () => {
    setDeleteQConfirmOpen(true);
  };

  const handleConfirmDeleteQuestion = async () => {
    await formationQuestionnaireApi.deleteQuestion(adminId, question.id);
    setDeleteQConfirmOpen(false);
    onRefresh();
  };

  const handleAddAnswer = async () => {
    if (!newAnswerText.trim()) return;
    setSavingAnswer(true);
    try {
      await formationQuestionnaireApi.createAnswer(
        adminId,
        question.id,
        newAnswerText,
        question.answers?.length || 0,
        newAnswerCorrect,
      );
      setNewAnswerText('');
      setNewAnswerCorrect(false);
      setAddingAnswer(false);
      onRefresh();
    } finally {
      setSavingAnswer(false);
    }
  };

  const hasCorrectAnswer = (question.answers || []).some((a) => a.is_correct);

  return (
    <Accordion disableGutters sx={{ mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center" width="100%">
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 24 }}>
            {index + 1}.
          </Typography>
          {!hasCorrectAnswer && (
            <Chip label="Pas de bonne réponse" color="error" size="small" />
          )}
          <Typography variant="body2" sx={{ flexGrow: 1 }}>
            {question.question}
          </Typography>
          <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
            <IconButton size="small" onClick={() => setEditing(!editing)}><EditIcon fontSize="small" /></IconButton>
            <IconButton size="small" color="error" onClick={handleDeleteQuestion}><DeleteIcon fontSize="small" /></IconButton>
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {/* Edition du texte */}
        {editing && (
          <Box mb={2} p={1} bgcolor="grey.50" borderRadius={1}>
            <TextField
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              fullWidth
              multiline
              rows={2}
              size="small"
            />
            <Stack direction="row" spacing={1} mt={1}>
              <Button size="small" variant="contained" onClick={handleSaveQuestion} disabled={saving}>
                {saving ? <CircularProgress size={14} /> : 'Enregistrer'}
              </Button>
              <Button size="small" onClick={() => setEditing(false)}>Annuler</Button>
            </Stack>
          </Box>
        )}

        {/* Réponses */}
        <Typography variant="caption" color="text.secondary" mb={1} display="block">
          Réponses proposées – marquer la bonne réponse avec ✓
        </Typography>
        {(question.answers || []).map((a) => (
          <AnswerItem key={a.id} answer={a} adminId={adminId} onRefresh={onRefresh} />
        ))}

        {/* Ajout réponse */}
        {addingAnswer ? (
          <Box mt={1} p={1.5} bgcolor="grey.50" borderRadius={1}>
            <TextField
              label="Texte de la réponse"
              value={newAnswerText}
              onChange={(e) => setNewAnswerText(e.target.value)}
              fullWidth
              size="small"
              autoFocus
            />
            <FormControlLabel
              control={
                <Radio
                  checked={newAnswerCorrect}
                  onChange={() => setNewAnswerCorrect(!newAnswerCorrect)}
                  size="small"
                />
              }
              label="Bonne réponse"
              sx={{ mt: 1 }}
            />
            <Stack direction="row" spacing={1} mt={1}>
              <Button size="small" variant="contained" onClick={handleAddAnswer} disabled={savingAnswer || !newAnswerText.trim()}>
                {savingAnswer ? <CircularProgress size={14} /> : 'Ajouter'}
              </Button>
              <Button size="small" onClick={() => setAddingAnswer(false)}>Annuler</Button>
            </Stack>
          </Box>
        ) : (
          <Button
            size="small"
            startIcon={<AddIcon />}
            sx={{ mt: 1 }}
            onClick={() => setAddingAnswer(true)}
          >
            Ajouter une réponse
          </Button>
        )}
      </AccordionDetails>

      <Dialog open={deleteQConfirmOpen} onClose={() => setDeleteQConfirmOpen(false)}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>Supprimer cette question et toutes ses réponses ? Cette action est irréversible.</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteQConfirmOpen(false)}>Annuler</Button>
          <Button color="error" variant="contained" onClick={handleConfirmDeleteQuestion}>Supprimer</Button>
        </DialogActions>
      </Dialog>
    </Accordion>
  );
}

// ===========================================================================
// AnswerItem : une réponse
// ===========================================================================

function AnswerItem({ answer, adminId, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [answerText, setAnswerText] = useState(answer.answer);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await formationQuestionnaireApi.modifyAnswer(adminId, answer.id, { answer: answerText });
      setEditing(false);
      onRefresh();
    } finally {
      setSaving(false);
    }
  };

  const handleSetCorrect = async () => {
    await formationQuestionnaireApi.modifyAnswer(adminId, answer.id, { is_correct: !answer.is_correct });
    onRefresh();
  };

  const handleDelete = async () => {
    await formationQuestionnaireApi.deleteAnswer(adminId, answer.id);
    onRefresh();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        p: 1,
        mb: 0.5,
        borderRadius: 1,
        bgcolor: answer.is_correct ? 'success.50' : 'background.paper',
        border: '1px solid',
        borderColor: answer.is_correct ? 'success.main' : 'divider',
      }}
    >
      {/* Bouton bonne réponse */}
      <Tooltip title={answer.is_correct ? 'Bonne réponse' : 'Marquer comme bonne réponse'}>
        <IconButton size="small" onClick={handleSetCorrect} color={answer.is_correct ? 'success' : 'default'}>
          {answer.is_correct
            ? <CheckCircleIcon fontSize="small" />
            : <RadioButtonUncheckedIcon fontSize="small" />}
        </IconButton>
      </Tooltip>

      {editing ? (
        <>
          <TextField
            value={answerText}
            onChange={(e) => setAnswerText(e.target.value)}
            size="small"
            sx={{ flexGrow: 1, mx: 1 }}
          />
          <Button size="small" variant="contained" onClick={handleSave} disabled={saving} sx={{ mr: 0.5 }}>
            {saving ? <CircularProgress size={14} /> : 'OK'}
          </Button>
          <Button size="small" onClick={() => setEditing(false)}>✕</Button>
        </>
      ) : (
        <>
          <Typography variant="body2" sx={{ flexGrow: 1, mx: 1 }}>
            {answer.answer}
          </Typography>
          <IconButton size="small" onClick={() => setEditing(true)}><EditIcon fontSize="small" /></IconButton>
          <IconButton size="small" color="error" onClick={handleDelete}><DeleteIcon fontSize="small" /></IconButton>
        </>
      )}
    </Box>
  );
}

// ===========================================================================
// ResultsDialog : résultats des apprenants
// ===========================================================================

function ResultsDialog({ open, onClose, results, loading, questionnaireName }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Résultats – {questionnaireName}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : results ? (
          <>
            {/* Stats globales */}
            <Stack direction="row" spacing={2} mb={3} flexWrap="wrap">
              <StatCard label="Score moyen" value={`${results.stats?.average_score ?? 0} %`} />
              <StatCard label="Apprenants" value={results.stats?.total_learners ?? 0} />
              {Object.entries(results.stats?.level_distribution || {}).map(([level, count]) => (
                <StatCard key={level} label={level} value={count} color={LEVEL_COLORS[level]} />
              ))}
            </Stack>

            {/* Tableau détail */}
            {results.results?.length > 0 ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Apprenant</TableCell>
                    <TableCell align="right">Score</TableCell>
                    <TableCell align="center">Niveau</TableCell>
                    <TableCell align="center">Validé</TableCell>
                    <TableCell align="right">Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.results.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell>{r.user_firstname} {r.user_lastname}</TableCell>
                      <TableCell align="right">{r.score_percent.toFixed(1)} %</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={r.level}
                          size="small"
                          sx={{ bgcolor: LEVEL_COLORS[r.level], color: '#fff', fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell align="center">{r.passed ? '✓' : '✗'}</TableCell>
                      <TableCell align="right">
                        {r.submitted_at ? new Date(r.submitted_at).toLocaleDateString('fr-FR') : '–'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Alert severity="info">Aucun résultat pour ce questionnaire.</Alert>
            )}
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
}

function StatCard({ label, value, color }) {
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: color || 'divider',
        borderRadius: 2,
        px: 2,
        py: 1,
        minWidth: 100,
        textAlign: 'center',
      }}
    >
      <Typography variant="h5" fontWeight="bold" color={color || 'text.primary'}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Box>
  );
}
